import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ShieldCheck, Crown, User as UserIcon, Trash2, Clock, Eye, EyeOff, Truck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { CircleUserRound } from "lucide-react";

const STATUS_PILL = {
  active:  "bg-emerald-100 text-emerald-800 border-emerald-200",
  pending: "bg-amber-100 text-amber-800 border-amber-200",
};

export default function TeamPage() {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, partnerResponse] = await Promise.all([api.get("/admin/users"), api.get("/endpoint-partners")]);
      setUsers(data); setPartners(partnerResponse.data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const approve = async (u) => {
    try { await api.post(`/admin/users/${u.id}/approve`); toast.success(`Approved ${u.email}`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const setRole = async (u, role) => {
    // Guardrail: promoting to Super Admin grants access to Residual Payout controls.
    if (role === "super_admin" && !window.confirm(
      `Promote ${u.email} to Super Admin?\n\nSuper Admins can see the Residual Payout dashboard and promote or demote other Super Admins. Only do this for the app owner.`
    )) return;
    if (role !== "super_admin" && u.role === "super_admin" && !window.confirm(
      `Demote ${u.email} from Super Admin to ${role}?\n\nThey will lose access to the Residual Payout dashboard.`
    )) return;
    try { await api.post(`/admin/users/${u.id}/role`, { role }); toast.success(`${u.email} → ${role.replace("_", " ")}`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email}? This deletes their account and revokes any active session.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Removed"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  // Show/hide a member on the public "Meet The Team" Essence roster.
  const toggleTeamVisibility = async (u) => {
    const hidden = u.hidden_from_team === false;
    setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, hidden_from_team: hidden } : x)));
    try {
      await api.post(`/admin/users/${u.id}/team-visibility`, { hidden });
      toast.success(hidden ? `${u.name || u.email} hidden from Essence` : `${u.name || u.email} shown on Essence`);
    } catch (err) {
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, hidden_from_team: !hidden } : x)));
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  // A user appears on the Essence "Meet The Team" roster when they are active
  // personnel, or specifically David Anderson Jr.
  const onEssenceRoster = (u) => {
    const role = u.role || "personnel";
    const isDavidJr = /david\s+anderson\s*,?\s*jr/i.test(u.name || "");
    return isDavidJr || (role === "personnel" && (u.status || "active") !== "pending");
  };

  const pending = users.filter(u => (u.status || "active") === "pending");
  const active = users.filter(u => (u.status || "active") !== "pending");

  // Only an existing Super Admin can promote/demote other Super Admins.
  const canManageSuperAdmin = currentUser?.role === "super_admin";
  const editMemberProfile = (member) => {
    const email = (member.email || "").toLowerCase();
    const partner = partners.find((p) => p.user_id === member.id || (p.email || "").toLowerCase() === email || (p.contact_email || "").toLowerCase() === email);
    navigate(partner ? `/partners?edit=${partner.id}` : `/profile?userId=${member.id}`);
  };

  const Row = ({ u }) => {
    const isSelf = u.id === currentUser?.id;
    const status = u.status || "active";
    const role = u.role || "personnel";
    return (
      <tr className="border-t border-[var(--border)]" data-testid={`user-row-${u.id}`}>
        <td className="p-3">
          <div className="flex items-center gap-3">
            {u.picture
              ? <img src={u.picture} alt="" className="w-8 h-8 rounded-full" />
              : <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500"><UserIcon size={14} /></div>}
            <div>
              <div className="font-medium capitalize flex items-center gap-1.5">
                {u.name || "—"}
                {isSelf && <span className="text-[10px] uppercase tracking-wide text-[var(--brand)]">you</span>}
              </div>
              <div className="text-xs text-gray-500">{u.email}</div>
            </div>
          </div>
        </td>
        <td className="p-3">
          <span className={`status-pill capitalize ${STATUS_PILL[status] || "bg-gray-100 text-gray-700 border-gray-200"}`}>{status}</span>
        </td>
        <td className="p-3">
          <span className={`capitalize font-medium inline-flex items-center gap-1 ${role === "super_admin" ? "text-amber-700" : ""}`}>
            {role === "super_admin" && <Crown size={12} className="text-amber-500" />}
            {role.replace("_", " ")}
          </span>
          <div className="text-xs text-gray-400">{u.auth_provider || "password"}</div>
        </td>
        <td className="p-3 text-xs text-gray-500">
          {u.last_login_at ? u.last_login_at.slice(0, 10) : (u.created_at?.slice(0, 10) || "—")}
        </td>
        <td className="p-3 text-right whitespace-nowrap space-x-1">
          <Button size="sm" variant="ghost" title={"Edit " + (u.name || u.email) + " profile"} onClick={() => editMemberProfile(u)} data-testid={`team-profile-${u.id}`}><CircleUserRound size={16} /></Button>
          {(
            <button
              type="button"
              onClick={() => toggleTeamVisibility(u)}
              title={u.hidden_from_team === false ? "Shown on the Essence page — click to hide" : "Hidden from the Essence page — click to show"}
              className={`inline-flex items-center justify-center w-8 h-8 rounded-md border transition-colors align-middle ${u.hidden_from_team === false ? "border-emerald-300 text-emerald-600 hover:bg-emerald-50" : "border-gray-300 text-gray-400 hover:bg-gray-50"}`}
              data-testid={`toggle-essence-${u.id}`}
            >
              {u.hidden_from_team === false ? <Eye size={15} /> : <EyeOff size={15} />}
            </button>
          )}
          {status === "pending" && (
            <Button size="sm" className="btn-brand" onClick={() => approve(u)} data-testid={`approve-${u.id}`}>
              <CheckCircle2 size={12} className="mr-1" /> Approve
            </Button>
          )}
          {status === "active" && !isSelf && (
            <>
              {role === "super_admin" ? (
                canManageSuperAdmin && (
                  <Button size="sm" variant="outline" onClick={() => setRole(u, "admin")} data-testid={`demote-super-${u.id}`}>
                    Demote to Admin
                  </Button>
                )
              ) : role === "admin" ? (
                <>
                  {canManageSuperAdmin && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-amber-700 border-amber-300 hover:bg-amber-50"
                      onClick={() => setRole(u, "super_admin")}
                      data-testid={`promote-super-${u.id}`}
                    >
                      <Crown size={12} className="mr-1" /> Make Super Admin
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => setRole(u, "personnel")} data-testid={`demote-${u.id}`}>Demote</Button>
                  <Button size="sm" variant="outline" onClick={() => setRole(u, "guest")} data-testid={`promote-guest-${u.id}`}>Make Guest</Button>
                </>
              ) : role === "distributing_partner" ? (
                <>
                  <Button size="sm" variant="outline" onClick={() => setRole(u, "personnel")} data-testid={`demote-dp-${u.id}`}>Demote</Button>
                  <Button size="sm" variant="outline" onClick={() => setRole(u, "admin")} data-testid={`promote-${u.id}`}><ShieldCheck size={12} className="mr-1" /> Make Admin</Button>
                  <Button size="sm" variant="outline" onClick={() => setRole(u, "guest")} data-testid={`promote-guest-${u.id}`}>Make Guest</Button>
                </>
              ) : (
                <>
                  <Button size="sm" variant="outline" onClick={() => setRole(u, "admin")} data-testid={`promote-${u.id}`}>
                    <ShieldCheck size={12} className="mr-1" /> Make Admin
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setRole(u, "distributing_partner")} data-testid={`promote-dp-${u.id}`}>
                    <Truck size={12} className="mr-1" /> Make DP
                  </Button>
                  {role !== "guest" && <Button size="sm" variant="outline" onClick={() => setRole(u, "guest")} data-testid={`promote-guest-${u.id}`}>Make Guest</Button>}
                  {canManageSuperAdmin && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-amber-700 border-amber-300 hover:bg-amber-50"
                      onClick={() => setRole(u, "super_admin")}
                      data-testid={`promote-super-${u.id}`}
                    >
                      <Crown size={12} className="mr-1" /> Make Super Admin
                    </Button>
                  )}
                </>
              )}
            </>
          )}
          {!isSelf && (
            <Button size="sm" variant="ghost" onClick={() => remove(u)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`remove-${u.id}`}>
              <Trash2 size={12} />
            </Button>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="p-3 sm:p-6 lg:p-10 max-w-full">
      <header className="mb-6">
        <p className="label-eyebrow">Access control</p>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Team</h1>
        <p className="text-gray-500 mt-1 text-sm">
          Auto-approve list: <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">@mpiredistribution.biz</code> + <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">jaytoe.tukanu@outlook.com</code>. Everyone else lands here as pending.
        </p>
      </header>

      {loading && <p className="text-gray-400">Loading…</p>}

      {pending.length > 0 && (
        <Card className="p-0 mb-6 overflow-hidden border-amber-300" data-testid="pending-users">
          <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 flex items-center gap-2">
            <Clock size={14} className="text-amber-700" />
            <span className="label-eyebrow text-amber-800">Awaiting approval · {pending.length}</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-soft)] text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left p-3">User</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Role</th>
                <th className="text-left p-3">Signed up</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>{pending.map(u => <Row key={u.id} u={u} />)}</tbody>
          </table>
        </Card>
      )}

      <Card className="p-0 overflow-hidden" data-testid="active-users">
        <div className="px-4 py-3 border-b border-[var(--border)]">
          <span className="label-eyebrow">Active team · {active.length}</span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg-soft)] text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="text-left p-3">User</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Role</th>
              <th className="text-left p-3">Last active</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>{active.map(u => <Row key={u.id} u={u} />)}</tbody>
        </table>
      </Card>
    </div>
  );
}
